import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/config/site";
import { getPlatform } from "@/lib/device";
import { LogoMark } from "./Logo";

const ROLES = [
  { value: "artist", label: "Artist" },
  { value: "manager", label: "Manager" },
  { value: "team", label: "Music team" },
  { value: "label", label: "Label" },
];
const PLATFORMS = [
  { value: "ios", label: "iPhone" },
  { value: "android", label: "Android" },
];
const PLAN_NAMES = { starter: "Artist Starter", pro: "Artist Pro", elite: "Artist Elite" };

const Chips = ({ options, value, onChange, name }) => (
  <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        data-testid={`waitlist-${name}-${o.value}`}
        onClick={() => onChange(value === o.value ? null : o.value)}
        className={`h-10 border px-4 text-sm font-medium transition-colors duration-200 ${
          value === o.value ? "border-pa-orange bg-pa-orange text-pa-black" : "border-white/15 text-pa-ink hover:border-white/40"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Success = ({ message }) => (
  <div data-testid="waitlist-success-message" className="py-6">
    <div className="grid h-12 w-12 place-items-center bg-pa-orange text-pa-black"><Check className="h-6 w-6" /></div>
    <p className="mt-6 font-display text-5xl uppercase leading-none">You&apos;re on the list.</p>
    <p className="mt-4 text-pa-muted">{message}</p>
  </div>
);

export const WaitlistDialog = ({ open, plan, source, onOpenChange }) => {
  const [form, setForm] = useState({ email: "", name: "", role: null, platform: null });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  useEffect(() => {
    if (!open) return;
    const p = getPlatform();
    setForm((f) => ({ ...f, platform: f.platform || (p === "desktop" ? null : p) }));
    setStatus((s) => (s.state === "done" ? s : { state: "idle", message: "" }));
  }, [open]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        email: form.email,
        name: form.name || null,
        role: form.role,
        platform: form.platform,
        plan: plan || null,
        source: source || "site",
      });
      setStatus({ state: "done", message: data.message });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? "Please enter a valid email address." : detail || "Something went wrong. Please try again.";
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="waitlist-modal"
        data-lenis-prevent
        className="max-h-[92vh] max-w-[520px] overflow-y-auto rounded-none border-white/10 bg-pa-surface p-7 text-pa-ink sm:rounded-none sm:p-9"
      >
        {status.state === "done" ? (
          <>
            <DialogTitle className="sr-only">Waitlist confirmation</DialogTitle>
            <DialogDescription className="sr-only">You have joined the Pitchally waitlist.</DialogDescription>
            <Success message={status.message} />
          </>
        ) : (
          <form onSubmit={submit} className="grid gap-6" noValidate>
            <div>
              <div className="flex items-center gap-3">
                <LogoMark className="h-6 w-6" />
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-pa-orange-text">Launching soon · iOS & Android</span>
              </div>
              <DialogTitle className="mt-5 font-display text-5xl font-normal uppercase leading-[0.9] tracking-normal">
                Get Pitchally first.
              </DialogTitle>
              <DialogDescription className="mt-3 text-[15px] leading-relaxed text-pa-muted">
                The app isn&apos;t in the stores yet. Join the waitlist and we&apos;ll send your download link the day it lands.
              </DialogDescription>
              {plan && (
                <p data-testid="waitlist-selected-plan" className="mt-4 inline-block border border-pa-orange/60 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-pa-orange-text">
                  Interested in: {PLAN_NAMES[plan]}
                </p>
              )}
            </div>

            <label className="grid gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-pa-muted">Email address *</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(e) => set("email")(e.target.value)}
                placeholder="you@yourmusic.com"
                data-testid="waitlist-email-input"
                className="h-12 border border-white/15 bg-pa-black px-4 text-base text-pa-ink placeholder:text-pa-dim focus:border-pa-orange focus:outline-none"
              />
            </label>
            <label className="grid gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-pa-muted">Artist or full name</span>
              <input
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                data-testid="waitlist-name-input"
                className="h-12 border border-white/15 bg-pa-black px-4 text-base text-pa-ink focus:border-pa-orange focus:outline-none"
              />
            </label>
            <div className="grid gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-pa-muted">I am a</span>
              <Chips name="role" options={ROLES} value={form.role} onChange={set("role")} />
            </div>
            <div className="grid gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-pa-muted">My phone</span>
              <Chips name="platform" options={PLATFORMS} value={form.platform} onChange={set("platform")} />
            </div>

            {status.state === "error" && (
              <p role="alert" data-testid="waitlist-error-message" className="text-sm text-red-400">{status.message}</p>
            )}

            <button
              type="submit"
              disabled={status.state === "loading"}
              data-testid="waitlist-submit-button"
              className="flex h-14 items-center justify-center gap-2 bg-pa-orange text-[13px] font-semibold uppercase tracking-[0.16em] text-pa-black shadow-hard transition-[transform,box-shadow] duration-300 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-hard-lg disabled:opacity-70"
            >
              {status.state === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
              Join the waitlist
            </button>
            <p className="text-xs text-pa-dim">Only launch news. No spam. Unsubscribe anytime.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
